import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useHome from './dashboard/useHome';
import DashboardView from './dashboard/DashboardView';
import ServicesManagerDialog from '../components/services/ServicesManagerDialog';
import WelcomeResumeBanner from '../components/WelcomeResumeBanner';
import ContextualHint from '../components/ContextualHint';
import { useNotificationCenter } from '../contexts/NotificationCenterContext';
import { useContextualHint } from '../hooks/useContextualHint';
import { useUpdateOnboardingStats } from '../hooks/useOnboarding';
import dashboardService from '../services/dashboardService';

/**
 * A HOME do Admin — container.
 *
 * Toda a decisão de o que aparece vem de `GET /api/v1/home` (decisão D2 do redesenho): ordem,
 * peso, estado e o resumo de cada escopo chegam prontos. Aqui não existe `&&` decidindo se um
 * card existe — se voltar a existir, a decisão foi quebrada e ajustar a Home volta a exigir
 * deploy do front.
 *
 * O desenho é da `DashboardView` (Claude Design). Este arquivo só liga os fios.
 *
 * ⚠️ Sem `PageLayout`: a View desenha a página inteira, incluindo a saudação do topo. Envolver
 * em PageLayout duplicaria o cabeçalho — é o mesmo padrão de Ausências e Suporte.
 */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { vm, actions, registrarEvento } = useHome();
  const { openNotificationCenter } = useNotificationCenter();
  const [servicesManagerOpen, setServicesManagerOpen] = useState(false);

  /**
   * `/dashboard/stats` continua sendo carregado — e NÃO é sobra de refactor.
   *
   * Dois consumidores dependem do retrato CRU da conta, e nenhum deles é card:
   *   · `useUpdateOnboardingStats` — esta página é o ÚNICO alimentador do contexto de onboarding;
   *   · `WelcomeResumeBanner` — decide auto-abrir o assistente para conta estritamente vazia,
   *     com trava anti-loop e a regra de NÃO abrir quando a carga falhou.
   *
   * Mover esses dois sinais para o contrato da Home é dívida registrada, não faxina para agora:
   * errar o auto-launch joga um cliente com dados dentro do wizard.
   */
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsErrored, setStatsErrored] = useState(false);

  useEffect(() => {
    let vivo = true;
    dashboardService.getStats()
      .then((dados) => { if (vivo) setStats(dados); })
      .catch(() => { if (vivo) setStatsErrored(true); })
      .finally(() => { if (vivo) setStatsLoading(false); });
    return () => { vivo = false; };
  }, []);

  useUpdateOnboardingStats(stats, statsLoading);
  const hint = useContextualHint('dashboard', { stats });

  /**
   * Os dois eventos locais do contrato (`action.type === 'emit'`).
   *
   * Existem porque nem todo clique é navegação: o contador de Serviços abre um diálogo e "Ver
   * todos" os avisos abre o painel lateral. Sem `emit`, os dois virariam rota — mudando o
   * produto por limitação do contrato.
   */
  useEffect(() => {
    registrarEvento('openServicesManager', () => setServicesManagerOpen(true));
    registrarEvento('openNotifications', () => openNotificationCenter());
  }, [registrarEvento, openNotificationCenter]);

  return (
    <>
      {/* Conta vazia que caiu no Admin sem passar pelo Welcome → auto-abre o assistente. */}
      <WelcomeResumeBanner stats={stats} loading={statsLoading} errored={statsErrored} />

      {hint && (
        <ContextualHint
          hint={hint}
          onActionClick={hint?.action?.link ? () => {
            // `/?newuser=true` reentra no gate do App.jsx (mount effect) → precisa de reload
            // real; o navigate (SPA) não redispararia a detecção de newuser.
            if (hint.action.link.includes('newuser=true')) {
              window.location.href = hint.action.link;
            } else {
              navigate(hint.action.link);
            }
          } : undefined}
        />
      )}

      <DashboardView vm={vm} actions={actions} />

      <ServicesManagerDialog
        open={servicesManagerOpen}
        onClose={() => setServicesManagerOpen(false)}
      />
    </>
  );
}
